import React, { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import styles from "./Job.module.scss";
import classNames from "classnames/bind";
import { PlusIcon, MenuVerticalIcon } from "~/assets/icons";
import pencilIcon from "~/assets/icons/pencil-icon.png";
import { useTranslation } from "react-i18next";
import { useAppDispatch, useAppSelector } from "~/redux/hooks";
import { jobActions } from "~/thunks/job/jobSlice";
import { notify } from "~/utils/helper";
import BaseTabMenu from "~/components/common/base-tab-menu/BaseTabMenu";
import BaseSelectButton from "~/components/common/base-select-button/BaseSelectButton";
import JobSortFilter, { JobSortFilterValue } from "~/components/common/job-sort-filter/JobSortFilter";
import KanbanView from "./kanban-view/KanbanView";
import { useNavigate } from "react-router-dom";
import {
  handleDeleteJob,
  handleGetBoardCount,
  handleGetJobs,
  handleMoveStage,
  handleUpdateJob,
} from "~/thunks/job/jobThunk";
import JobListTable from "./list-view/JobListTable";
import { IJob } from "~/utils/interface/job";
import dayjs from "dayjs";
import LoadingMainContent from "~/components/common/loading-main-content/LoadingMainContent";
import PrimaryButton from "~/components/common/primary-button/PrimaryButton";
import ModalAlertDelete from "~/components/common/modal-alert-delete/ModalAlertDelete";
import BaseModalV2 from "~/components/common/base-modal-v2/BaseModalV2";
import { adminRouteAbsolute } from "~/utils/constants/route";
import { ColumnType } from "~/utils/types/common";
import EditJobModal from "./job-detail/tabs/overview/component/edit-job-details/EditJobModal";
import { handleGetOverviewJobDetails, handleUpdateOverviewJobDetails } from "~/thunks/job-details/jobDetailsThunk";
import userManagementApi, { ISalesRep } from "~/apis/userManagement";
import jobApi, { ITradeType } from "~/apis/job";
import { IPayloadUpdateOverviewJobDetails } from "~/utils/interface/jobDetails";
import { getStatusFromJobStage } from "~/utils/helpers/jobStage";

const cx = classNames.bind(styles);

const JOB_BOARDS_CONFIG = [
  { label: "Sales", value: "SALES" },
  { label: "Production", value: "PRODUCTION" },
  { label: "Billing", value: "BILLING" },
];

const JOB_STAGES_CONFIG = {
  Sales: [
    { label: "Lead", value: "LEAD" },
    { label: "Appointment Scheduled", value: "APPOINTMENT_SCHEDULED" },
    { label: "Filling Claim", value: "FILLING_CLAIM" },
    { label: "Waiting on Scope", value: "WAITING_ON_SCOPE" },
    { label: "Supplementing", value: "SUPPLEMENTING" },
    { label: "Estimating", value: "ESTIMATING" },
    { label: "Estimate Sent", value: "ESTIMATE_SENT" },
    { label: "Commercial", value: "COMMERCIAL" },
    { label: "Hold-Signing", value: "HOLD_SIGNING" },
    { label: "Signed Contract", value: "SIGNED_CONTRACT" },
    { label: "Production Review", value: "PRODUCTION_REVIEW" },
    { label: "Lost", value: "LOST" },
  ],
  Production: [
    { label: "Production Review", value: "PRODUCTION_REVIEW" },
    { label: "Material Ordered", value: "MATERIAL_ORDERED" },
    { label: "Secondary Trades", value: "SECONDARY_TRADES" },
    { label: "Job Scheduled", value: "JOB_SCHEDULED" },
    { label: "Job Complete", value: "JOB_COMPLETED" }
  ],
  Billing: [
    { label: "Job Complete", value: "JOB_COMPLETED" },
    { label: "Invoiced Insurance", value: "INVOICED_INSURANCE" },
    { label: "Invoiced Commercial", value: "INVOICED_COMMERCIAL" },
    { label: "Paid & Closed", value: "PAID_N_CLOSED" },
    { label: "Paid & Happy", value: "PAID_N_HAPPY" },
    { label: "Paid & Left Review", value: "PAID_N_LEFT_REVIEW" },
    {
      label: "DELINQUENT - Do not add jobs",
      value: "DELINQUENT_DO_NOT_ADD_JOBS",
    },
  ],
};

interface KanbanColumnData {
  stage: string;
  jobs: IJob[];
  loading: boolean;
  error: string | null;
}

// Helper function to find stage object by label or value
const findStageByLabelOrValue = (
  stageLabelOrValue: string,
  board: keyof typeof JOB_STAGES_CONFIG
): { label: string; value: string } | null => {
  const stages = JOB_STAGES_CONFIG[board];
  if (!stages) return null;
  return (
    stages.find(
      (stage) =>
        stage.label === stageLabelOrValue || stage.value === stageLabelOrValue
    ) || null
  );
};

const Job = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Sales");
  const [currentView, setCurrentView] = useState("kanban");
  const [sortFilter, setSortFilter] = useState<JobSortFilterValue>({
    type: 'all',
  });
  const [tabCounts, setTabCounts] = useState<{ [key: string]: number }>({
    Sales: 0,
    Production: 0,
    Billing: 0,
  });
  const [kanbanData, setKanbanData] = useState<
    Record<string, KanbanColumnData>
  >({});
  const [isLoading, setIsLoading] = useState(false);
  const [rollbackInProgress, setRollbackInProgress] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [jobToDelete, setJobToDelete] = useState<IJob | null>(null);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [jobToEditNotes, setJobToEditNotes] = useState<ExtendedJob | null>(null);
  const [notesValue, setNotesValue] = useState("");
  const [openActionMenuId, setOpenActionMenuId] = useState<string | null>(null);
  const [optimisticStatus, setOptimisticStatus] = useState<Record<string, string>>({});
  const [openStageDropdownId, setOpenStageDropdownId] = useState<string | null>(null);
  const [stageUpdating, setStageUpdating] = useState<Record<string, boolean>>({});
  const [optimisticStage, setOptimisticStage] = useState<Record<string, string>>({});
  const { jobs, currentPage, totalPages, totalItems, needRefreshData, showAddJobModal } = useAppSelector(
    (state) => state.job
  );
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { ChevronDown } = require("lucide-react");

  const viewOptions = [
    {
      id: "kanban",
      label: "Kanban View",
      icon: ChevronDown,
    },
    {
      id: "list",
      label: "List View",
      icon: ChevronDown,
    },
  ];
  const { t } = useTranslation(["admin/job", "admin/component"]);
  const [showEditJobModal, setShowEditJobModal] = useState(false);
  const [jobDetailForEdit, setJobDetailForEdit] = useState<any>(null);
  const [salesReps, setSalesReps] = useState<ISalesRep[]>([]);
  const [tradeTypes, setTradeTypes] = useState<ITradeType[]>([]);
  const [isLoadingJobDetail, setIsLoadingJobDetail] = useState(false);

  // Helper function to get stage label from jobStage value
  const getStageLabel = (jobStage?: string): string => {
    if (!jobStage) return "";
    
    // Search through all boards to find matching stage
    for (const board of Object.keys(JOB_STAGES_CONFIG)) {
      const stages = JOB_STAGES_CONFIG[board as keyof typeof JOB_STAGES_CONFIG];
      const stage = stages.find((s) => s.value === jobStage);
      if (stage) return stage.label;
    }
    return jobStage;
  };

  // Extended job type for Production mode with additional fields
  type ExtendedJob = IJob & {
    salesRep?: {
      id?: string;
      username?: string;
      profile?: {
        firstName?: string;
        lastName?: string;
        color?: string;
        avatar?: any;
      };
    };
    primaryContact?: {
      name?: string;
    };
    numberOfSquares?: string | number;
    pitch?: string;
    tradeTypes?: Array<{ id?: string; name?: string }>;
    jobNote?: string;
  };

  // Status is derived from job stage and updates automatically when stage changes.
  const StatusBadge = ({ job }: { job: IJob }) => {
    const pendingStage = optimisticStage[job.id];
    const displayStatus =
      optimisticStatus[job.id] ||
      (pendingStage ? getStatusFromJobStage(pendingStage) : job.status);

    const getStatusColor = (status: string | undefined) => {
      switch (status) {
        case "In Progress":
          return "#ffb800";
        case "Pending":
          return "#ff4444";
        case "Completed":
          return "#00b795";
        case "Cancelled":
          return "#6b7280";
        default:
          return "#ff4444";
      }
    };

    return (
      <div className="relative flex items-center justify-center">
        <div
          className="h-8 rounded-[25px] flex items-center justify-center px-3 w-fit"
          style={{ backgroundColor: getStatusColor(displayStatus) }}
        >
          <p className="m-0 text-xs font-normal text-white whitespace-nowrap overflow-hidden text-ellipsis">
            {displayStatus}
          </p>
        </div>
      </div>
    );
  };

  // Stage Cell Component
  const StageCell = ({ job }: { job: IJob | ExtendedJob }) => {
    const isOpen = openStageDropdownId === job.id;
    const isUpdating = stageUpdating[job.id] || false;
    const displayStage = optimisticStage[job.id] || job.jobStage;
    const stageLabel = getStageLabel(displayStage);
    const triggerRef = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState<{ top: number; left: number; showAbove?: boolean; bottom?: number }>({ top: 0, left: 0 });
    
    // Get all stage options from all boards (Sales, Production, Billing)
    const allStageOptions = [
      ...JOB_STAGES_CONFIG.Sales,
      ...JOB_STAGES_CONFIG.Production,
      ...JOB_STAGES_CONFIG.Billing,
    ];
    
    // Remove duplicates based on value (in case same stage appears in multiple boards)
    const stageOptions = allStageOptions.filter(
      (stage, index, self) => index === self.findIndex((s) => s.value === stage.value)
    );

    // Calculate dropdown position when opening
    useEffect(() => {
      if (isOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        const dropdownHeight = 300; // max height of dropdown
        const spaceBelow = window.innerHeight - rect.bottom;
        const spaceAbove = rect.top;
        
        // If not enough space below but enough space above, show dropdown above
        const showAbove = spaceBelow < dropdownHeight && spaceAbove > spaceBelow;
        
        // Calculate left position (ensure dropdown doesn't go off screen)
        let left = rect.left;
        const dropdownWidth = 200; // min width
        if (left + dropdownWidth > window.innerWidth) {
          left = window.innerWidth - dropdownWidth - 10; // 10px padding from edge
        }
        if (left < 10) {
          left = 10; // 10px padding from left edge
        }
        
        if (showAbove) {
          // Show above: calculate bottom position from viewport bottom to trigger top
          const bottom = window.innerHeight - rect.top + 4;
          setDropdownPosition({
            top: 0, // Will use bottom instead
            left: left,
            showAbove: true,
            bottom: bottom,
          });
        } else {
          // Show below: use top position
          setDropdownPosition({
            top: rect.bottom + 4,
            left: left,
            showAbove: false,
            bottom: undefined,
          });
        }
      }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (
          triggerRef.current &&
          !triggerRef.current.contains(event.target as Node) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setOpenStageDropdownId(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const handleToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (isUpdating) return;
      setOpenStageDropdownId(isOpen ? null : job.id);
    };

    const handleStageChange = async (newStage: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (newStage === displayStage || isUpdating) {
        setOpenStageDropdownId(null);
        return;
      }

      const derivedStatus = getStatusFromJobStage(newStage);

      // Optimistic update: update UI immediately
      setOptimisticStage((prev) => ({
        ...prev,
        [job.id]: newStage,
      }));
      setOptimisticStatus((prev) => ({
        ...prev,
        [job.id]: derivedStatus,
      }));
      setStageUpdating((prev) => ({
        ...prev,
        [job.id]: true,
      }));
      setOpenStageDropdownId(null);

      try {
        await dispatch(
          handleUpdateJob({
            id: job.id,
            data: { jobStage: newStage },
          })
        ).unwrap();
        
        // Remove optimistic update on success (data will be refreshed)
        setOptimisticStage((prev) => {
          const updated = { ...prev };
          delete updated[job.id];
          return updated;
        });
        setOptimisticStatus((prev) => {
          const updated = { ...prev };
          delete updated[job.id];
          return updated;
        });
        setStageUpdating((prev) => {
          const updated = { ...prev };
          delete updated[job.id];
          return updated;
        });
        
        notify("success", "Stage updated successfully!");
        dispatch(jobActions.changeNeedRefreshData(true));
      } catch (error: any) {
        // Rollback optimistic update on error
        setOptimisticStage((prev) => {
          const updated = { ...prev };
          delete updated[job.id];
          return updated;
        });
        setOptimisticStatus((prev) => {
          const updated = { ...prev };
          delete updated[job.id];
          return updated;
        });
        setStageUpdating((prev) => {
          const updated = { ...prev };
          delete updated[job.id];
          return updated;
        });
        notify("error", `Failed to update stage: ${error?.message || "Unknown error"}`);
      }
    };

    const ChevronDownIcon = () => (
      <svg width="6" height="5" viewBox="0 0 9 6" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M4.243 5.656L0 1.414L1.415 0L4.243 2.828L7.071 0L8.486 1.414L4.243 5.656Z" fill="#F9FBFE"/>
      </svg>
    );

    return (
      <>
        <div ref={triggerRef} className="relative flex items-center justify-center">
          <div
            className={`h-8 rounded-[25px] flex items-center justify-center gap-2 px-3 w-fit cursor-pointer transition-opacity duration-200 ${
              isUpdating ? "opacity-80 cursor-not-allowed" : "hover:opacity-90"
            }`}
            style={{ backgroundColor: "#ff4444" }}
            onClick={handleToggle}
          >
            {isUpdating ? (
              <div className="flex items-center justify-center">
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <>
                <p className="m-0 text-xs font-normal text-white whitespace-nowrap overflow-hidden text-ellipsis">
                  {stageLabel}
                </p>
                <div className="flex items-center justify-center w-2 h-2" style={{ marginLeft: "4px" }}>
                  <ChevronDownIcon />
                </div>
              </>
            )}
          </div>
        </div>
        {isOpen && !isUpdating && typeof document !== "undefined" && createPortal(
          <div
            ref={dropdownRef}
            className="fixed bg-[#030e1f] border border-[#1f2937] rounded-lg shadow-lg z-[10001] min-w-[200px] max-w-[300px]"
            style={{
              top: dropdownPosition.showAbove ? undefined : `${dropdownPosition.top}px`,
              bottom: dropdownPosition.showAbove && dropdownPosition.bottom ? `${dropdownPosition.bottom}px` : undefined,
              left: `${dropdownPosition.left}px`,
              maxHeight: "300px",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            {stageOptions.map((option, index) => (
              <button
                key={option.value}
                className={`w-full px-4 py-3 bg-transparent border-none text-center text-sm font-normal text-white cursor-pointer transition-colors whitespace-nowrap ${
                  index !== stageOptions.length - 1 ? "border-b border-[#1f2937]" : ""
                } ${
                  option.value === displayStage
                    ? "bg-[#1f2937] text-[#2d80fc]"
                    : "hover:bg-[#1f2937]"
                }`}
                onClick={(e) => handleStageChange(option.value, e)}
              >
                {option.label}
              </button>
            ))}
          </div>,
          document.body
        )}
      </>
    );
  };

  // Action Menu Component
  const ActionMenu = ({ job }: { job: IJob }) => {
    const isOpen = openActionMenuId === job.id;
    const triggerRef = useRef<HTMLButtonElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, right: 0 });

    // Calculate dropdown position when opening
    useEffect(() => {
      if (isOpen && triggerRef.current) {
        const rect = triggerRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.bottom + 4,
          right: window.innerWidth - rect.right,
        });
      }
    }, [isOpen]);

    // Close dropdown when clicking outside
    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        if (
          triggerRef.current &&
          !triggerRef.current.contains(event.target as Node) &&
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target as Node)
        ) {
          setOpenActionMenuId(null);
        }
      };

      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [isOpen]);

    const handleToggle = (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpenActionMenuId(isOpen ? null : job.id);
    };

    const handleDetail = (e: React.MouseEvent) => {
      e.stopPropagation();
      navigate(adminRouteAbsolute.job.detail(job.id));
      setOpenActionMenuId(null);
    };

    const handleEdit = async (e: React.MouseEvent) => {
      e.stopPropagation();
      setOpenActionMenuId(null);
      
      // Fetch job detail before opening modal
      setIsLoadingJobDetail(true);
      try {
        // Fetch job detail
        const jobDetailResponse = await dispatch(
          handleGetOverviewJobDetails(job.id)
        ).unwrap();
        
        // Set job detail and open modal
        setJobDetailForEdit(jobDetailResponse.data);
        setShowEditJobModal(true);
      } catch (error: any) {
        notify("error", `Failed to load job details: ${error?.message || "Unknown error"}`);
      } finally {
        setIsLoadingJobDetail(false);
      }
    };

    const handleDelete = (e: React.MouseEvent) => {
      e.stopPropagation();
      handleJobDelete(job);
      setOpenActionMenuId(null);
    };

    return (
      <>
        <div className="relative flex items-center justify-center">
          <button
            ref={triggerRef}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-transparent border-none cursor-pointer transition-colors hover:bg-white/10"
            onClick={handleToggle}
          >
            <MenuVerticalIcon className="w-5 h-5 text-white" />
          </button>
        </div>
        {isOpen && typeof document !== "undefined" && createPortal(
          <div
            ref={dropdownRef}
            className="fixed !bg-[#071427] border border-[#1f2937] rounded-lg shadow-lg z-[10001] w-[229px]"
            style={{
              top: `${dropdownPosition.top}px`,
              right: `${dropdownPosition.right}px`,
            }}
          >
            <button
              className="w-full px-4 py-3 bg-transparent border-none text-center text-sm font-normal text-white cursor-pointer transition-colors border-b border-[#111E33] hover:bg-[#1f2937]"
              onClick={handleDetail}
            >
              View
            </button>
            <button
              className="w-full px-4 py-3 bg-transparent border-none text-center text-sm font-normal text-white cursor-pointer transition-colors border-b border-[#111E33] hover:bg-[#1f2937]"
              onClick={handleEdit}
            >
              Edit
            </button>
            <button
              className="w-full px-4 py-3 bg-transparent border-none text-center text-sm font-normal text-white cursor-pointer transition-colors border-b border-[#111E33] hover:bg-[rgba(255,68,68,0.1)]"
              onClick={handleDelete}
            >
              Delete
            </button>
          </div>,
          document.body
        )}
      </>
    );
  };

  // Columns for Sales and Billing modes
  const salesBillingColumns: Array<ColumnType> = [
    {
      id: "project",
      labelHeader: "Project",
      width: "17.9%",
      renderCell: (job: IJob, index: number) => (
        <div className={cx("project-cell")}>
          <p className={cx("project-name")}>{job.title || ""}</p>
          <p className={cx("address")}>{job.jobAddress}</p>
        </div>
      ),
    },
    {
      id: "priority",
      labelHeader: "Priority",
      width: "7.9%",
      renderCell: (job: IJob, index: number) => (
        <div className={cx("priority-cell", `${job.priority}`)}>
          <p>{job.priority?.toLocaleLowerCase() || ""}</p>
        </div>
      ),
    },
    {
      id: "materials",
      labelHeader: "Materials",
      width: "19.9%",
      renderCell: (job: IJob, index: number) => {
        const materials = job.jobMaterials || [];
        const maxVisible = 3;
        const visibleMaterials = materials.slice(0, maxVisible);
        const remainingCount = materials.length - maxVisible;

        return (
          <div className={cx("materials-cell")}>
            {visibleMaterials.map((material, idx) => (
              <p key={idx} className={cx("material-item")} title={material}>
                <span>{material}</span>
              </p>
            ))}
            {remainingCount > 0 && (
              <p className={cx("material-item", "more-indicator")} title={materials.slice(maxVisible).join(", ")}>
                <span>+{remainingCount} more</span>
              </p>
            )}
          </div>
        );
      },
    },
    {
      id: "salesRep",
      labelHeader: "Sales Rep",
      width: "8.9%",
      renderCell: (job: IJob, index: number) => (
        <div className={cx("sales-rep-cell")}>
          <div
            className={cx("avatar")}
            style={{
              backgroundColor:
                job.assignedAccount?.color || "#C4C4C4",
            }}
          />

          <p className={cx("sales-rep-name")}>
            {job.assignedAccount?.username || ""}
          </p>
        </div>
      ),
    },
    {
      id: "stage",
      labelHeader: "Stage",
      width: "14.6%",
      renderCell: (job: IJob, index: number) => <StageCell job={job} />,
    },
    {
      id: "createdDate",
      labelHeader: "Created Date",
      width: "7.9%",
      renderCell: (job: IJob, index: number) => (
        <p className={cx("created-date")}>
          {job.createdAt ? dayjs(job.createdAt).format("YYYY-MM-DD") : ""}
        </p>
      ),
    },
    {
      id: "dueDate",
      labelHeader: "Due Date",
      width: "7.9%",
      renderCell: (job: IJob, index: number) => (
        <p className={cx("due-date")}>
          {job?.dueDate ? dayjs(job?.dueDate).format("YYYY-MM-DD") : "No due date yet."}
        </p>
      ),
    },
    {
      id: "status",
      labelHeader: "Status",
      width: "11.9%",
      renderCell: (job: IJob, index: number) => <StatusBadge job={job} />,
    },
    {
      id: "actions",
      labelHeader: "",
      width: "3.0%",
      renderCell: (job: IJob, index: number) => <ActionMenu job={job} />,
    },
  ];

  // Columns for Production mode
  // Width ratios based on: Project: 220px, Customer Name: 160px, Sales Rep: 160px, 
  // # of Squares: 120px, Roof Pitch: 100px, Trades: 120px, Due Date: 120px, 
  // Priority: 100px, Stage: 150px, Notes: 250px, Action: remaining space
  // Total fixed: 1500px, converted to percentages maintaining ratios
  const productionColumns: Array<ColumnType> = [
    {
      id: "project",
      labelHeader: "Project",
      width: "13.67%", // 220/1500
      renderCell: (job: ExtendedJob, index: number) => (
        <div className={cx("project-cell")}>
          <p className={cx("project-name")}>{job.title || ""}</p>
          <p className={cx("address")}>{job.jobAddress}</p>
        </div>
      ),
    },
    {
      id: "customerName",
      labelHeader: "Customer Name",
      width: "10.67%", // 160/1500
      renderCell: (job: ExtendedJob, index: number) => (
        <div className={cx("customer-name-cell")}>
          <div
            className={cx("avatar")}
            style={{
              backgroundColor: "#C4C4C4",
            }}
          />
          <p className={cx("customer-name")}>
            {(job as any).primaryContact?.name || (job as any).customerName || "---"}
          </p>
        </div>
      ),
    },
    {
      id: "salesRep",
      labelHeader: "Sales Rep",
      width: "10.67%", // 160/1500
      renderCell: (job: ExtendedJob, index: number) => (
        <div className={cx("sales-rep-cell")}>
          <div
            className={cx("avatar")}
            style={{
              backgroundColor:
                job.salesRep?.profile?.color || job.assignedAccount?.color || "#C4C4C4",
            }}
          />
          <p className={cx("sales-rep-name")}>
            {job.salesRep?.profile?.firstName && job.salesRep?.profile?.lastName
              ? `${job.salesRep.profile.firstName} ${job.salesRep.profile.lastName}`
              : job.salesRep?.username || job.assignedAccount?.username || ""}
          </p>
        </div>
      ),
    },
    {
      id: "numberOfSquares",
      labelHeader: "# of Squares",
      width: "8%", // 120/1500
      renderCell: (job: ExtendedJob, index: number) => (
        <p className={cx("squares-cell")}>
          {job.numberOfSquares || (job as any).numberOfSquares || "---"}
        </p>
      ),
    },
    {
      id: "roofPitch",
      labelHeader: "Roof Pitch",
      width: "6.67%", // 100/1500
      renderCell: (job: ExtendedJob, index: number) => (
        <p className={cx("pitch-cell")}>
          {job.pitch || (job as any).pitch || "---"}
        </p>
      ),
    },
    {
      id: "trades",
      labelHeader: "Trades",
      width: "8%", // 120/1500
      renderCell: (job: ExtendedJob, index: number) => (
        <div className={cx("trades-cell")}>
          {job.tradeTypes && job.tradeTypes.length > 0 ? (
            job.tradeTypes.map((trade, idx) => (
              <span key={idx} className={cx("trade-badge")}>
                {trade.name || trade.id || ""}
              </span>
            ))
          ) : (job as any).tradeType ? (
            <span className={cx("trade-badge")}>{(job as any).tradeType}</span>
          ) : (
            "---"
          )}
        </div>
      ),
    },
    {
      id: "dueDate",
      labelHeader: "Due Date",
      width: "8%", // 120/1500
      renderCell: (job: ExtendedJob, index: number) => (
        <p className={cx("due-date")}>
          {job?.dueDate ? dayjs(job.dueDate).format("YYYY-MM-DD") : "---"}
        </p>
      ),
    },
    {
      id: "priority",
      labelHeader: "Priority",
      width: "6.67%", // 100/1500
      renderCell: (job: ExtendedJob, index: number) => (
        <div className={cx("priority-cell", `${job.priority}`)}>
          <p>{job.priority?.toLocaleLowerCase() || ""}</p>
        </div>
      ),
    },
    {
      id: "stage",
      labelHeader: "Stage",
      width: "11%", // 150/1500
      renderCell: (job: ExtendedJob, index: number) => <StageCell job={job} />,
    },
    {
      id: "notes",
      labelHeader: "Notes",
      width: "13.67%", // 250/1500
      renderCell: (job: ExtendedJob, index: number) => {
        const notesText = job.description || (job as any).description || "";
        return (
          <div className={cx("notes-cell")}>
            <div className={cx("notes-display", !notesText && "empty")}>
              {notesText || "Notes..."}
            </div>
          <div 
            className={cx("notes-icon")}
            onClick={(e) => {
              e.stopPropagation();
              setJobToEditNotes(job);
              setNotesValue(job.description || (job as any).description || "");
              setShowNotesModal(true);
            }}
          >
            <img 
              src={pencilIcon} 
              alt="Edit notes" 
              style={{ width: "24px", height: "24px", minWidth: "24px", minHeight: "24px" }}
            />
          </div>
        </div>
        );
      },
    },
    {
      id: "actions",
      labelHeader: "",
      width: "3%", // Remaining space
      renderCell: (job: ExtendedJob, index: number) => <ActionMenu job={job} />,
    },
  ];

  // Select columns based on active tab
  const columns = activeTab === "Production" ? productionColumns : salesBillingColumns;


  useEffect(() => {
    return () => {
      dispatch(jobActions.setCurrentPage(1));
    };
  }, [dispatch]);

  // Fetch Sales Reps and Trade Types on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch Sales Reps
        const salesRepsResponse = await userManagementApi.getSalesReps();
        if (salesRepsResponse.data?.data?.responses) {
          setSalesReps(salesRepsResponse.data.data.responses);
        }

        // Fetch Trade Types
        const tradeTypesResponse = await jobApi.getTradeTypes();
        if (tradeTypesResponse.data?.data?.responses) {
          setTradeTypes(tradeTypesResponse.data.data.responses);
        }
      } catch (error) {
        console.error("Error fetching salesReps and tradeTypes:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (needRefreshData || dispatch) {
      dispatch(handleGetBoardCount())
        .unwrap()
        .then((res: any) => {
          setTabCounts({
            Sales: res.data.sales || 0,
            Production: res.data.production || 0,
            Billing: res.data.billing || 0,
          });
        })
        .catch((error) => {
          notify("error", `Failed to fetch board count: ${error}`);
        });
    }
  }, [needRefreshData, dispatch]);

  useEffect(() => {
    if (needRefreshData || dispatch || currentView === "list") {
      setIsLoading(true);

      // Build API params based on sortFilter
      const params: any = {
        page: currentPage,
        limit: itemsPerPage,
      };

      // Add jobBoard filter based on activeTab
      if (activeTab && JOB_BOARDS_CONFIG.find(board => board.label === activeTab)) {
        const boardConfig = JOB_BOARDS_CONFIG.find(board => board.label === activeTab);
        if (boardConfig) {
          params.jobBoard = boardConfig.value;
        }
      }

      // Add jobType filter if selected (support single or array)
      if (sortFilter.type === 'jobType' && sortFilter.value) {
        if (Array.isArray(sortFilter.value)) {
          // Multiple selections: jobType[]=value1&jobType[]=value2
          params['jobType[]'] = sortFilter.value;
        } else {
          // Single selection: jobType=value
          params.jobType = sortFilter.value;
        }
      }

      // Add tradeType filter if selected
      if (sortFilter.type === 'tradeType' && sortFilter.value) {
        params.tradeTypes = [parseInt(sortFilter.value as string)];
      }

      // Add salesRepId filter if selected (support single or array)
      if (sortFilter.type === 'salesRep' && sortFilter.value) {
        if (Array.isArray(sortFilter.value)) {
          // Multiple selections: salesRepId[]=id1&salesRepId[]=id2
          params['salesRepId[]'] = sortFilter.value.map(id => parseInt(id));
        } else {
          // Single selection: salesRepId=id
          params.salesRepId = parseInt(sortFilter.value as string);
        }
      }

      // Add assigneeId filter if selected (support single or array)
      if (sortFilter.type === 'assignee' && sortFilter.value) {
        if (Array.isArray(sortFilter.value)) {
          // Multiple selections: assigneeId[]=id1&assigneeId[]=id2
          params['assigneeId[]'] = sortFilter.value.map(id => parseInt(id));
        } else {
          // Single selection: assigneeId=id
          params.assigneeId = parseInt(sortFilter.value as string);
        }
      }

      // Add subcontractorId filter if selected (support single or array)
      if (sortFilter.type === 'subcontractor' && sortFilter.value) {
        if (Array.isArray(sortFilter.value)) {
          // Multiple selections: subcontractorId[]=id1&subcontractorId[]=id2
          params['subcontractorId[]'] = sortFilter.value.map(id => parseInt(id));
        } else {
          // Single selection: subcontractorId=id
          params.subcontractorId = parseInt(sortFilter.value as string);
        }
      }

      dispatch(handleGetJobs(params))
        .unwrap()
        .then(() => {
          dispatch(jobActions.changeNeedRefreshData(false));
        })
        .catch((error) => {
          notify("error", `Failed to fetch jobs: ${error}`);
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [needRefreshData, dispatch, currentPage, activeTab, currentView, sortFilter, itemsPerPage]);

  const handleClickCreateJob = async () => {
    dispatch(jobActions.setShowAddJobModal(true));
  };

  const fetchKanbanData = useCallback(async () => {
    const stages =
      JOB_STAGES_CONFIG[activeTab as keyof typeof JOB_STAGES_CONFIG];
    if (!stages || stages.length === 0) return;

    try {
      setIsLoading(true);

      const initialData: Record<string, KanbanColumnData> = {};
      stages.forEach((stage) => {
        initialData[stage.label] = {
          stage: stage.label,
          jobs: [],
          loading: true,
          error: null,
        };
      });
      setKanbanData(initialData);

      // Build filter params from sortFilter
      const buildFilterParams = (baseParams: any) => {
        const params = { ...baseParams };

        // Add jobType filter if selected (support single or array)
        if (sortFilter.type === 'jobType' && sortFilter.value) {
          if (Array.isArray(sortFilter.value)) {
            params['jobType[]'] = sortFilter.value;
          } else {
            params.jobType = sortFilter.value;
          }
        }

        // Add tradeType filter if selected
        if (sortFilter.type === 'tradeType' && sortFilter.value) {
          params.tradeTypes = [parseInt(sortFilter.value as string)];
        }

        // Add salesRepId filter if selected (support single or array)
        if (sortFilter.type === 'salesRep' && sortFilter.value) {
          if (Array.isArray(sortFilter.value)) {
            params['salesRepId[]'] = sortFilter.value.map(id => parseInt(id));
          } else {
            params.salesRepId = parseInt(sortFilter.value as string);
          }
        }

        // Add assigneeId filter if selected (support single or array)
        if (sortFilter.type === 'assignee' && sortFilter.value) {
          if (Array.isArray(sortFilter.value)) {
            params['assigneeId[]'] = sortFilter.value.map(id => parseInt(id));
          } else {
            params.assigneeId = parseInt(sortFilter.value as string);
          }
        }

        // Add subcontractorId filter if selected (support single or array)
        if (sortFilter.type === 'subcontractor' && sortFilter.value) {
          if (Array.isArray(sortFilter.value)) {
            params['subcontractorId[]'] = sortFilter.value.map(id => parseInt(id));
          } else {
            params.subcontractorId = parseInt(sortFilter.value as string);
          }
        }

        return params;
      };

      const promises = stages.map((stage) => {
        const baseParams = {
          jobStage: stage.value,
          page: 1,
        };
        const params = buildFilterParams(baseParams);
        return dispatch(handleGetJobs(params)).unwrap();
      });

      const results = await Promise.allSettled(promises);

      const updatedData: Record<string, KanbanColumnData> = {};

      results.forEach((result, index) => {
        const stage = stages[index];

        if (result.status === "fulfilled") {
          updatedData[stage.label] = {
            stage: stage.label,
            jobs: result.value?.responses || [],
            loading: false,
            error: null,
          };
        } else {
          updatedData[stage.label] = {
            stage: stage.label,
            jobs: [],
            loading: false,
            error: result.reason?.message || "Failed to load data",
          };
          console.error(`Error fetching stage ${stage.label}:`, result.reason);
        }
      });

      setKanbanData(updatedData);
      dispatch(jobActions.changeNeedRefreshData(false));
    } catch (error) {
      console.error("Error in fetchKanbanData:", error);
      notify("error", "Failed to load kanban data");
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, dispatch, sortFilter]);

  useEffect(() => {
    if (currentView === "kanban") {
      fetchKanbanData();
    }
  }, [currentView, activeTab, needRefreshData, fetchKanbanData]);

  const handleTabChange = (tabId: string) => {
    setActiveTab(tabId);
  };

  const tabs = [
    { id: "Sales", label: "Sales", count: tabCounts.Sales },
    { id: "Production", label: "Production", count: tabCounts.Production },
    { id: "Billing", label: "Billing", count: tabCounts.Billing },
  ];

  const handlePageChange = (newPage: number) => {
    setIsLoading(true);

    // Build API params with current sortFilter
    const params: any = {
      page: newPage,
      limit: itemsPerPage,
    };

    // Add jobBoard filter based on activeTab
    if (activeTab && JOB_BOARDS_CONFIG.find(board => board.label === activeTab)) {
      const boardConfig = JOB_BOARDS_CONFIG.find(board => board.label === activeTab);
      if (boardConfig) {
        params.jobBoard = boardConfig.value;
      }
    }

    if (sortFilter.type === 'jobType' && sortFilter.value) {
      params.jobType = sortFilter.value;
    }

    if (sortFilter.type === 'tradeType' && sortFilter.value) {
      const tradeTypeValue = Array.isArray(sortFilter.value) ? sortFilter.value[0] : sortFilter.value;
      params.tradeTypes = [parseInt(tradeTypeValue as string)];
    }

    if (sortFilter.type === 'salesRep' && sortFilter.value) {
      params.salesRepSearch = sortFilter.value;
    }

    if (sortFilter.type === 'assignee' && sortFilter.value) {
      params.assigneeSearch = sortFilter.value;
    }

    dispatch(handleGetJobs(params))
      .unwrap()
      .catch((error) => {
        notify("error", `Failed to fetch jobs: ${error}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleItemsPerPageChange = (newItemsPerPage: number) => {
    setIsLoading(true);
    
    // Reset to page 1 when changing items per page
    dispatch(jobActions.setCurrentPage(1));

    // Build API params with current sortFilter
    const params: any = {
      page: 1,
      limit: newItemsPerPage,
    };

    // Add jobBoard filter based on activeTab
    if (activeTab && JOB_BOARDS_CONFIG.find(board => board.label === activeTab)) {
      const boardConfig = JOB_BOARDS_CONFIG.find(board => board.label === activeTab);
      if (boardConfig) {
        params.jobBoard = boardConfig.value;
      }
    }

    if (sortFilter.type === 'jobType' && sortFilter.value) {
      params.jobType = sortFilter.value;
    }

    if (sortFilter.type === 'tradeType' && sortFilter.value) {
      const tradeTypeValue = Array.isArray(sortFilter.value) ? sortFilter.value[0] : sortFilter.value;
      params.tradeTypes = [parseInt(tradeTypeValue as string)];
    }

    if (sortFilter.type === 'salesRep' && sortFilter.value) {
      params.salesRepSearch = sortFilter.value;
    }

    if (sortFilter.type === 'assignee' && sortFilter.value) {
      params.assigneeSearch = sortFilter.value;
    }

    setItemsPerPage(newItemsPerPage);

    dispatch(handleGetJobs(params))
      .unwrap()
      .catch((error) => {
        notify("error", `Failed to fetch jobs: ${error}`);
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleRowClick = (job: IJob) => {
    navigate(adminRouteAbsolute.job.detail(job.id));
  };

  const handleJobMove = async (
    jobId: string,
    fromStage: string,
    toStage: string,
    job: any
  ) => {
    // Store original state for potential rollback
    const originalKanbanData = JSON.parse(JSON.stringify(kanbanData));

    // Find stage objects (fromStage and toStage are labels from UI)
    const toStageObj = findStageByLabelOrValue(
      toStage,
      activeTab as keyof typeof JOB_STAGES_CONFIG
    );

    if (!toStageObj) {
      notify("error", "Invalid stage selected");
      return;
    }

    try {
      // Optimistic update: immediately update the UI
      setKanbanData((prevData) => {
        const newData = { ...prevData };

        // Remove job from source stage
        if (newData[fromStage]) {
          newData[fromStage] = {
            ...newData[fromStage],
            jobs: newData[fromStage].jobs.filter((j: any) => j.id !== jobId),
          };
        }

        // Add job to destination stage
        if (newData[toStage]) {
          const updatedJob = { ...job, jobStage: toStage };
          newData[toStage] = {
            ...newData[toStage],
            jobs: [...newData[toStage].jobs, updatedJob],
          };
        }

        return newData;
      });

      // Background API call - use value for API
      await dispatch(
        handleMoveStage({
          id: jobId,
          params: { jobStage: toStageObj.value },
        })
      ).unwrap();

      // Update board counts after successful move
      try {
        const res = await dispatch(handleGetBoardCount()).unwrap();
        setTabCounts({
          Sales: res.data.sales || 0,
          Production: res.data.production || 0,
          Billing: res.data.billing || 0,
        });
      } catch (countError) {
        console.error("Failed to update board count:", countError);
        // Don't rollback for board count failure, just log the error
      }
    } catch (error) {
      console.error("Failed to move job:", error);

      // Set rollback state for visual feedback
      setRollbackInProgress(true);

      // Rollback optimistic update on API failure
      setKanbanData(originalKanbanData);

      // Show error notification with rollback info
      notify(
        "error",
        `Failed to move job. Item has been moved back to original position.`
      );

      // Clear rollback state after a short delay
      setTimeout(() => {
        setRollbackInProgress(false);
      }, 2000);

      throw error;
    }
  };


  const handleJobDelete = (job: IJob) => {
    setJobToDelete(job);
    setShowDeleteModal(true);
  };

  const handleJobEdit = async (job: IJob) => {
    // Fetch job detail before opening modal
    setIsLoadingJobDetail(true);
    try {
      // Fetch job detail
      const jobDetailResponse = await dispatch(
        handleGetOverviewJobDetails(job.id)
      ).unwrap();
      
      // Set job detail and open modal
      setJobDetailForEdit(jobDetailResponse.data);
      setShowEditJobModal(true);
    } catch (error: any) {
      notify("error", `Failed to load job details: ${error?.message || "Unknown error"}`);
    } finally {
      setIsLoadingJobDetail(false);
    }
  };

  const handleConfirmDelete = async () => {
    if (!jobToDelete) return;

    try {
      await dispatch(handleDeleteJob(jobToDelete.id)).unwrap();
      notify("success", "Job deleted successfully!");
      setShowDeleteModal(false);
      setJobToDelete(null);
      dispatch(jobActions.changeNeedRefreshData(true));
    } catch (error: any) {
      const errorMessage = error?.message || "Failed to delete job!";
      notify("error", errorMessage);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteModal(false);
    setJobToDelete(null);
  };

  const handleOpenNotesModal = (job: ExtendedJob) => {
    setJobToEditNotes(job);
    setNotesValue(job.description || (job as any).description || "");
    setShowNotesModal(true);
  };

  const handleCloseNotesModal = () => {
    setShowNotesModal(false);
    setJobToEditNotes(null);
    setNotesValue("");
  };

  const handleSaveNotes = async () => {
    if (!jobToEditNotes) return;

    try {
      await dispatch(
        handleUpdateJob({
          id: jobToEditNotes.id,
          data: { description: notesValue },
        })
      ).unwrap();
      notify("success", "Notes updated successfully!");
      setShowNotesModal(false);
      setJobToEditNotes(null);
      setNotesValue("");
      dispatch(jobActions.changeNeedRefreshData(true));
    } catch (error: any) {
      notify("error", `Failed to update notes: ${error?.message || "Unknown error"}`);
    }
  };

  const handleCloseEditJobModal = () => {
    setShowEditJobModal(false);
    setJobDetailForEdit(null);
  };

  const handleSaveEditJob = async (data: IPayloadUpdateOverviewJobDetails) => {
    if (!jobDetailForEdit) return;
    
    try {
      await dispatch(
        handleUpdateOverviewJobDetails({ id: jobDetailForEdit.id, data })
      ).unwrap();
      notify("success", "Job updated successfully!");
      handleCloseEditJobModal();
      dispatch(jobActions.changeNeedRefreshData(true));
    } catch (error: any) {
      notify("error", `Failed to update job: ${error?.message || "Unknown error"}`);
    }
  };

  return (
    <div className={cx("jobs-container")}>
      {isLoading && (
        <div className={cx("loading-overlay")}>
          <LoadingMainContent />
        </div>
      )}
      <div className={cx("header")}>
        <div className={cx("header-item")}>
          <BaseTabMenu
            tabs={tabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
          />
        </div>

        <div className={cx("header-item")}>
          <BaseSelectButton
            viewOptions={viewOptions}
            selectedView={currentView}
            onViewChange={(viewId: string | number) =>
              setCurrentView(String(viewId))
            }
          />

          {(currentView === "list" || currentView === "kanban") && (
            <JobSortFilter
              value={sortFilter}
              onChange={setSortFilter}
              placeholder="Sort By"
            />
          )}

          <PrimaryButton
            title={t("addJob")}
            icon={<PlusIcon className="w-[16px] h-[16px] [&>path]:fill-white" />}
            iconPosition="left"
            onClick={handleClickCreateJob}
            className={cx("add-button")}
          />
        </div>
      </div>

      <div className={cx("content")}>
        {currentView === "kanban" ? (
          <KanbanView
            data={kanbanData}
            onJobMove={handleJobMove}
            onJobClick={handleRowClick}
            onJobEdit={handleJobEdit}
            onJobDelete={handleJobDelete}
            rollbackInProgress={rollbackInProgress}
          />
        ) : (
          <div className={cx("list-view")}>
            <JobListTable
              data={jobs}
              columns={columns}
              page={currentPage}
              totalPages={totalPages}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onChangePage={handlePageChange}
              onItemsPerPageChange={handleItemsPerPageChange}
              showPagination={true}
            />
          </div>
        )}
      </div>
      {showDeleteModal && jobToDelete && (
        <ModalAlertDelete
          open={showDeleteModal}
          title="Delete Job"
          description={`Are you sure you want to delete "${jobToDelete.title || "this job"}"? This action cannot be undone.`}
          onCancel={handleCancelDelete}
          onConfirm={handleConfirmDelete}
        />
      )}
      {showNotesModal && jobToEditNotes && (
        <BaseModalV2
          open={showNotesModal}
          onClose={handleCloseNotesModal}
          title="Edit Notes"
          width="600px"
          className="notes-modal"
        >
          <div className={cx("notes-modal-content")}>
            <textarea
              className={cx("notes-textarea")}
              placeholder="Notes..."
              value={notesValue}
              onChange={(e) => setNotesValue(e.target.value)}
              rows={8}
            />
            <div className={cx("notes-modal-actions")}>
              <button
                className={cx("notes-modal-button", "cancel")}
                onClick={handleCloseNotesModal}
              >
                Cancel
              </button>
              <button
                className={cx("notes-modal-button", "save")}
                onClick={handleSaveNotes}
              >
                Save
              </button>
            </div>
          </div>
        </BaseModalV2>
      )}
      {showEditJobModal && jobDetailForEdit && (
        <EditJobModal
          isOpen={showEditJobModal}
          jobDetail={jobDetailForEdit}
          salesReps={salesReps}
          tradeTypes={tradeTypes}
          onClose={handleCloseEditJobModal}
          onSave={handleSaveEditJob}
        />
      )}
    </div>
  );
};

export default Job;